/**
 * Rules service — thin Axios wrapper for the Phase 1b regelverk
 * (bank-reconciliation booking rules) endpoints, plus the pure
 * presentation/validation helpers the Rules Settings page uses.
 *
 * Mirrors MatchingService.js: pure functions that take a companyId +
 * payload and return axios promises. Callers own loading state and
 * errors — the backend may not be deployed yet, so every caller must
 * render an error state rather than crash.
 *
 * Contract (Phase 1b):
 *   GET    /:companyId/accounting/reconciliation/banking/rules
 *   POST   /:companyId/accounting/reconciliation/banking/rules
 *   PUT    /:companyId/accounting/reconciliation/banking/rules/:id
 *   DELETE /:companyId/accounting/reconciliation/banking/rules/:id
 *   POST   /:companyId/accounting/reconciliation/banking/rules/:id/toggle
 *   POST   /:companyId/accounting/reconciliation/banking/rules/install-pack
 */

import axios from './AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';

const config = { withCredentials: true };

// ---------------------------------------------------------------------------
// Labels (Swedish — raw enum codes must never reach the UI)
// ---------------------------------------------------------------------------

/** Account roles the rule engine can resolve per company. */
export const ROLE_LABELS = {
    primary_bank: 'Företagskonto (bank)',
    tax_account: 'Skattekonto',
    employee_tax: 'Personalskatt',
    social_fees: 'Sociala avgifter',
    corporate_tax_debt: 'Skatteskuld (bolagsskatt)',
    vat: 'Moms',
};

/** Amount kinds a rule row can book. */
export const AMOUNT_LABELS = {
    full: 'Hela beloppet',
    vat_25_net: 'Belopp exkl. moms (25 %)',
    vat_25: 'Momsdel (25 %)',
};

// Short forms for the one-line kontering summary in the list.
const AMOUNT_SHORT = {
    full: '',
    vat_25_net: 'exkl. moms',
    vat_25: 'moms 25 %',
};

export const DIRECTION_LABELS = {
    in: 'Pengar in',
    out: 'Pengar ut',
};

export const SIDE_LABELS = {
    debit: 'Debet',
    credit: 'Kredit',
};

export const STATEMENT_SOURCE_LABELS = {
    bank: 'Bank',
    tax_authority: 'Skattekonto',
};

/** The skattekonto starter pack (13+ rules, installed with auto off). */
export const SKATTEKONTO_PACK_ID = 'se_skattekonto_v1';

// ---------------------------------------------------------------------------
// Pure helpers (unit tested)
// ---------------------------------------------------------------------------

const isNum = (v) => v !== '' && v != null && Number.isFinite(parseFloat(v));

const formatKr = (v) => `${new Intl.NumberFormat('sv-SE').format(parseFloat(v))} kr`;

/**
 * One-line Swedish summary of a rule's flat AND-conditions, e.g.
 * 'Text innehåller "SKATTEVERKET" · Belopp 100–5 000 kr · Pengar ut'.
 * Empty conditions → 'Inga villkor (matchar allt)'.
 */
export function conditionsSummary(conditions = {}) {
    const parts = [];
    const c = conditions || {};
    if (c.text_contains) parts.push(`Text innehåller "${c.text_contains}"`);
    if (c.text_starts) parts.push(`Text börjar med "${c.text_starts}"`);
    if (c.text_ends) parts.push(`Text slutar med "${c.text_ends}"`);
    const hasMin = isNum(c.amount_min);
    const hasMax = isNum(c.amount_max);
    if (hasMin && hasMax) parts.push(`Belopp ${formatKr(c.amount_min)}–${formatKr(c.amount_max)}`);
    else if (hasMin) parts.push(`Belopp minst ${formatKr(c.amount_min)}`);
    else if (hasMax) parts.push(`Belopp högst ${formatKr(c.amount_max)}`);
    if (c.direction && DIRECTION_LABELS[c.direction]) parts.push(DIRECTION_LABELS[c.direction]);
    return parts.length > 0 ? parts.join(' · ') : 'Inga villkor (matchar allt)';
}

/**
 * One-line Swedish summary of the action rows, e.g.
 * '1630 Debet / Företagskonto (bank) Kredit' — amount kind appended in
 * parentheses when it isn't the full amount. Never shows raw codes.
 */
export function actionSummary(action = {}) {
    const rows = Array.isArray(action?.rows) ? action.rows : [];
    if (rows.length === 0) return 'Inga konteringsrader';
    return rows
        .map((row) => {
            const target = row?.account_number
                ? String(row.account_number)
                : (ROLE_LABELS[row?.role] || row?.role || '?');
            const side = SIDE_LABELS[row?.side] || row?.side || '?';
            const amountNote = AMOUNT_SHORT[row?.amount];
            return amountNote ? `${target} ${side} (${amountNote})` : `${target} ${side}`;
        })
        .join(' / ');
}

/**
 * Validate the rule form. `rows` use the editor shape:
 * { mode: 'account'|'role', account_number, role, side, amount }.
 *
 * Returns { valid, errors } where errors maps field → Swedish message.
 * Auto-apply REQUIRES a positive beloppstak (amount_cap) — a rule that
 * books without review must always be capped.
 */
export function validateRuleForm(form = {}) {
    const errors = {};
    if (!String(form.name || '').trim()) {
        errors.name = 'Ange ett namn på regeln';
    }

    const c = form.conditions || {};
    const hasCondition = Boolean(
        String(c.text_contains || '').trim()
        || String(c.text_starts || '').trim()
        || String(c.text_ends || '').trim()
        || isNum(c.amount_min)
        || isNum(c.amount_max)
        || c.direction,
    );
    if (!hasCondition) {
        errors.conditions = 'Ange minst ett villkor — en regel utan villkor skulle matcha alla transaktioner';
    }
    if (isNum(c.amount_min) && isNum(c.amount_max)
        && parseFloat(c.amount_min) > parseFloat(c.amount_max)) {
        errors.amount = 'Minsta belopp kan inte vara större än största belopp';
    }

    const rows = Array.isArray(form.rows) ? form.rows : [];
    if (rows.length === 0) {
        errors.rows = 'Lägg till minst en konteringsrad';
    } else if (rows.some((row) => (row.mode === 'role' ? !row.role : !row.account_number))) {
        errors.rows = 'Varje konteringsrad behöver ett konto eller en roll';
    }

    if (form.auto_apply && !(parseFloat(form.amount_cap) > 0)) {
        errors.amount_cap = 'Ange ett beloppstak — det krävs för auto-bokföring';
    }

    return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * Map the editor form to the API payload (contract shape minus id/hit
 * fields). Empty condition fields are stripped; numbers are coerced;
 * each action row carries EITHER account_number OR role, never both.
 * amount_cap is null unless auto_apply is on.
 */
export function buildRulePayload(form = {}) {
    const c = form.conditions || {};
    const conditions = {};
    if (String(c.text_contains || '').trim()) conditions.text_contains = String(c.text_contains).trim();
    if (String(c.text_starts || '').trim()) conditions.text_starts = String(c.text_starts).trim();
    if (String(c.text_ends || '').trim()) conditions.text_ends = String(c.text_ends).trim();
    if (isNum(c.amount_min)) conditions.amount_min = parseFloat(c.amount_min);
    if (isNum(c.amount_max)) conditions.amount_max = parseFloat(c.amount_max);
    if (c.direction) conditions.direction = c.direction;

    const rows = (Array.isArray(form.rows) ? form.rows : []).map((row) => ({
        ...(row.mode === 'role'
            ? { role: row.role }
            : { account_number: String(row.account_number) }),
        side: row.side,
        amount: row.amount || 'full',
    }));

    const action = { rows };
    if (String(form.series || '').trim()) action.series = String(form.series).trim();
    if (String(form.description_template || '').trim()) {
        action.description_template = String(form.description_template).trim();
    }

    return {
        name: String(form.name || '').trim(),
        active: form.active !== false,
        statement_source: form.statement_source || 'bank',
        priority: Number.isFinite(parseInt(form.priority, 10)) ? parseInt(form.priority, 10) : 100,
        conditions,
        action,
        auto_apply: Boolean(form.auto_apply),
        amount_cap: form.auto_apply && isNum(form.amount_cap) ? parseFloat(form.amount_cap) : null,
    };
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * GET /:companyId/accounting/reconciliation/banking/rules
 * Returns { success, rules: [...] } (see contract at the top).
 */
export async function listRules(companyId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/rules`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/rules
 * Body: rule payload (buildRulePayload output). Returns { success, rule? }.
 */
export async function createRule(companyId, payload) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/rules`);
    const { data } = await axios.post(url, payload, config);
    return data;
}

/**
 * PUT /:companyId/accounting/reconciliation/banking/rules/:id
 */
export async function updateRule(companyId, ruleId, payload) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/rules/${ruleId}`);
    const { data } = await axios.put(url, payload, config);
    return data;
}

/**
 * DELETE /:companyId/accounting/reconciliation/banking/rules/:id
 */
export async function deleteRule(companyId, ruleId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/rules/${ruleId}`);
    const { data } = await axios.delete(url, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/rules/:id/toggle
 * Flips `active` server-side. Returns { success, rule? }.
 */
export async function toggleRule(companyId, ruleId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/rules/${ruleId}/toggle`);
    const { data } = await axios.post(url, {}, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/rules/install-pack
 * Body: { pack_id } — installs a curated rule pack (13+ skattekonto
 * rules for the default pack; installed with auto-bokföring OFF).
 */
export async function installPack(companyId, packId = SKATTEKONTO_PACK_ID) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/rules/install-pack`);
    const { data } = await axios.post(url, { pack_id: packId }, config);
    return data;
}
